// v3E.3 — Emergency Profile editor + QR display + regenerate link + SOS history.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RefreshCw, Copy, Download, ShieldAlert, Siren, AlertOctagon, Plus, Trash2, ExternalLink, Loader2, Heart } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const EMPTY_CONTACT = { name: "", relation: "", phone: "", is_primary: false };

const formatAlerts = (sos) => {
    const c = sos.contacts_notified || [];
    const sms = c.filter((x) => x.channel === "sms").length;
    const wa = c.filter((x) => x.channel === "whatsapp").length;
    return `${sms} SMS · ${wa} WhatsApp`;
};

const EmergencyContent = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [sosEvents, setSosEvents] = useState([]);
    const [form, setForm] = useState({
        blood_group: "",
        allergies: "",
        chronic_conditions: "",
        current_medications_summary: "",
        organ_donor: false,
        medical_notes: "",
        emergency_contacts: [],
    });

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [p, s] = await Promise.all([
                api.get("/emergency-profile"),
                api.get("/sos/events"),
            ]);
            setProfile(p.data);
            setSosEvents(s.data.items || []);
            setForm({
                blood_group: p.data.blood_group || "",
                allergies: (p.data.allergies || []).join(", "),
                chronic_conditions: (p.data.chronic_conditions || []).join(", "),
                current_medications_summary: p.data.current_medications_summary || "",
                organ_donor: !!p.data.organ_donor,
                medical_notes: p.data.medical_notes || "",
                emergency_contacts: p.data.emergency_contacts || [],
            });
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const updateContact = (idx, patch) => {
        setForm((f) => {
            const next = [...f.emergency_contacts];
            next[idx] = { ...next[idx], ...patch };
            // ensure single primary
            if (patch.is_primary) {
                next.forEach((c, i) => { if (i !== idx) c.is_primary = false; });
            }
            return { ...f, emergency_contacts: next };
        });
    };

    const addContact = () => setForm((f) => ({ ...f, emergency_contacts: [...f.emergency_contacts, { ...EMPTY_CONTACT }] }));
    const removeContact = (idx) => setForm((f) => ({ ...f, emergency_contacts: f.emergency_contacts.filter((_, i) => i !== idx) }));

    const onSave = async () => {
        try {
            setSaving(true);
            const payload = {
                blood_group: form.blood_group || null,
                allergies: form.allergies.split(",").map((s) => s.trim()).filter(Boolean),
                chronic_conditions: form.chronic_conditions.split(",").map((s) => s.trim()).filter(Boolean),
                current_medications_summary: form.current_medications_summary || null,
                organ_donor: form.organ_donor,
                medical_notes: form.medical_notes || null,
                emergency_contacts: form.emergency_contacts.filter((c) => (c.name || "").trim() && (c.phone || "").trim()),
            };
            const { data } = await api.patch("/emergency-profile", payload);
            setProfile(data);
            toast.success("Emergency profile saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const onRegenerate = async () => {
        if (!window.confirm("Regenerate the public link? The old QR code and link will stop working.")) return;
        try {
            setRegenerating(true);
            await api.post("/emergency-profile/regenerate-token");
            await load();
            toast.success("Link refreshed — old QR no longer works");
        } catch (err) {
            toast.error("Could not regenerate");
        } finally { setRegenerating(false); }
    };

    const publicUrl = profile?.qr_token ? `${window.location.origin}/emergency/public/${profile.qr_token}` : "";
    const qrSrc = profile?.qr_token
        ? `${process.env.REACT_APP_BACKEND_URL}/api/emergency-profile/public/${profile.qr_token}/qr.png`
        : "";

    const copyLink = async () => {
        try { await navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); } catch { toast.error("Copy failed"); }
    };

    const downloadQr = async () => {
        try {
            const res = await api.get("/emergency-profile/qr", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "pills-emergency-qr.png";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("QR downloaded");
        } catch { toast.error("Download failed"); }
    };

    if (loading) {
        return <div className="grid gap-4 lg:grid-cols-[1fr_360px]"><div className="h-96 skeleton rounded-3xl" /><div className="h-96 skeleton rounded-3xl" /></div>;
    }

    return (
        <div className="grid gap-6 lg:grid-cols-[1fr_360px]" data-testid="emergency-page">
            <div className="space-y-6">
                <header>
                    <p className="label-uppercase">Emergency Profile</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Be ready for the unexpected</h1>
                    <p className="mt-2 max-w-xl text-sm text-zinc-600 dark:text-zinc-400">Fill in what an ER team would need: blood group, allergies, conditions, current meds, and one or more emergency contacts. Then print or save the QR code.</p>
                </header>

                <div className="space-y-4 rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-white/[0.03]" data-testid="emergency-medical-section">
                    <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">Medical info</h2>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <label className="block">
                            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Blood group</span>
                            <select value={form.blood_group} onChange={(e) => setForm((f) => ({ ...f, blood_group: e.target.value }))} className="input-clean mt-1" data-testid="emerg-bg">
                                <option value="">—</option>
                                {BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Organ donor</span>
                            <select value={form.organ_donor ? "yes" : "no"} onChange={(e) => setForm((f) => ({ ...f, organ_donor: e.target.value === "yes" }))} className="input-clean mt-1" data-testid="emerg-organ">
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                            </select>
                        </label>
                        <label className="block md:col-span-2">
                            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Allergies (comma-separated)</span>
                            <input value={form.allergies} onChange={(e) => setForm((f) => ({ ...f, allergies: e.target.value }))} placeholder="e.g. Penicillin, Peanuts" className="input-clean mt-1" data-testid="emerg-allergies" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Chronic conditions</span>
                            <input value={form.chronic_conditions} onChange={(e) => setForm((f) => ({ ...f, chronic_conditions: e.target.value }))} placeholder="e.g. Hypertension, Diabetes" className="input-clean mt-1" data-testid="emerg-chronic" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Current medications summary</span>
                            <textarea rows={2} value={form.current_medications_summary} onChange={(e) => setForm((f) => ({ ...f, current_medications_summary: e.target.value }))} placeholder="e.g. Telmisartan 40mg OD, Metformin 500mg BD" maxLength={500} className="input-clean mt-1" data-testid="emerg-meds" />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Medical notes</span>
                            <textarea rows={3} value={form.medical_notes} onChange={(e) => setForm((f) => ({ ...f, medical_notes: e.target.value }))} placeholder="Pacemaker, recent surgery, anything else first responders should know" maxLength={1000} className="input-clean mt-1" data-testid="emerg-notes" />
                        </label>
                    </div>
                </div>

                <div className="space-y-4 rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-white/[0.03]" data-testid="emergency-contacts-section">
                    <div className="flex items-center justify-between">
                        <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">Emergency contacts</h2>
                        <button onClick={addContact} className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50/40 px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:border-brand-400/30 dark:bg-brand-500/10 dark:text-brand-300" data-testid="emerg-add-contact">
                            <Plus className="h-3 w-3" /> Add contact
                        </button>
                    </div>
                    {form.emergency_contacts.length === 0 ? (
                        <p className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/40 px-4 py-6 text-center text-sm text-zinc-500 dark:border-white/10 dark:bg-white/[0.02] dark:text-zinc-400">
                            Add at least one — they'll be alerted when you trigger SOS.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {form.emergency_contacts.map((c, i) => (
                                <div key={i} className="grid grid-cols-1 gap-2 rounded-2xl border border-zinc-100 bg-zinc-50/40 p-3 md:grid-cols-[1fr_1fr_1fr_auto_auto] md:items-center dark:border-white/10 dark:bg-white/[0.02]" data-testid={`emerg-contact-${i}`}>
                                    <input value={c.name} onChange={(e) => updateContact(i, { name: e.target.value })} placeholder="Name" className="input-clean !py-1.5 !text-sm" data-testid={`emerg-cname-${i}`} />
                                    <input value={c.relation || ""} onChange={(e) => updateContact(i, { relation: e.target.value })} placeholder="Relation (spouse, son…)" className="input-clean !py-1.5 !text-sm" data-testid={`emerg-crel-${i}`} />
                                    <input value={c.phone} onChange={(e) => updateContact(i, { phone: e.target.value })} placeholder="+91..." className="input-clean !py-1.5 !text-sm" data-testid={`emerg-cphone-${i}`} />
                                    <label className="inline-flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-300">
                                        <input type="checkbox" checked={!!c.is_primary} onChange={(e) => updateContact(i, { is_primary: e.target.checked })} data-testid={`emerg-cprim-${i}`} className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500" />
                                        Primary
                                    </label>
                                    <button onClick={() => removeContact(i)} className="rounded-full p-1.5 text-zinc-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10" data-testid={`emerg-cdel-${i}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                    <button onClick={onSave} disabled={saving} className="btn-brand !py-2 !px-5 !text-sm" data-testid="emerg-save-btn">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {saving ? "Saving..." : "Save profile"}
                    </button>
                </div>

                {/* SOS history */}
                <div className="space-y-3 rounded-3xl border border-rose-100 bg-rose-50/30 p-6 dark:border-rose-500/20 dark:bg-rose-500/[0.05]" data-testid="sos-history-section">
                    <div className="flex items-center gap-2">
                        <span className="grid h-9 w-9 place-items-center rounded-2xl bg-gradient-to-br from-rose-600 to-red-600 text-white">
                            <Siren className="h-4 w-4" />
                        </span>
                        <div>
                            <p className="label-uppercase text-rose-700 dark:text-rose-300">SOS history</p>
                            <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">Past emergency alerts</h2>
                        </div>
                    </div>
                    {sosEvents.length === 0 ? (
                        <p className="rounded-2xl bg-white/60 px-4 py-6 text-center text-sm text-zinc-500 dark:bg-white/[0.04] dark:text-zinc-400">No SOS events yet — use the red SOS button in the bottom-left corner if you need help.</p>
                    ) : (
                        <ul className="space-y-2">
                            {sosEvents.map((s) => (
                                <li key={s.id} data-testid={`sos-event-${s.id}`} className="rounded-2xl border border-rose-200 bg-white px-4 py-3 text-sm dark:border-rose-500/30 dark:bg-zinc-900/60">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <p className="font-semibold text-zinc-900 dark:text-zinc-50">
                                            <AlertOctagon className="mr-1 inline-block h-4 w-4 text-rose-600" />
                                            {new Date(s.triggered_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                        </p>
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${s.status === "triggered" ? "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300" : s.status === "resolved" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-zinc-100 text-zinc-700 dark:bg-white/10 dark:text-zinc-300"}`}>{s.status}</span>
                                    </div>
                                    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">{formatAlerts(s)}{s.ambulance_called ? " · Ambulance called" : ""}{s.notes ? ` · ${s.notes}` : ""}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Right column — QR card */}
            <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start" data-testid="emergency-qr-aside">
                <div className="overflow-hidden rounded-3xl border border-brand-100 bg-gradient-to-br from-brand-50 via-white to-violet-50 p-5 shadow-soft dark:border-brand-400/20 dark:from-brand-500/10 dark:via-zinc-900 dark:to-violet-500/10">
                    <div className="flex items-center gap-2">
                        <ShieldAlert className="h-4 w-4 text-brand-700 dark:text-brand-300" />
                        <p className="label-uppercase">ER scan-ready QR</p>
                    </div>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">Print this and put it in your wallet. Anyone scanning it sees your key medical info (no login needed).</p>
                    {publicUrl ? (
                        <>
                            <div className="mx-auto mt-4 grid h-48 w-48 place-items-center rounded-2xl bg-white p-2 shadow-sm">
                                <img src={qrSrc} alt="Emergency QR" className="h-full w-full object-contain" data-testid="emergency-qr-img" />
                            </div>
                            <div className="mt-3 break-all rounded-xl bg-white/60 px-3 py-2 font-mono text-[10px] text-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-300" data-testid="emergency-public-url">{publicUrl}</div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <button onClick={copyLink} className="inline-flex items-center gap-1 rounded-full bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600" data-testid="emerg-copy-link"><Copy className="h-3 w-3" /> Copy</button>
                                <button onClick={downloadQr} className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-white/10 dark:bg-transparent dark:text-zinc-200 dark:hover:bg-white/5" data-testid="emerg-download-qr"><Download className="h-3 w-3" /> PNG</button>
                                <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-white/10 dark:bg-transparent dark:text-zinc-200 dark:hover:bg-white/5" data-testid="emerg-preview-link"><ExternalLink className="h-3 w-3" /> Preview</a>
                                <button onClick={onRegenerate} disabled={regenerating} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-300" data-testid="emerg-regen-link">
                                    <RefreshCw className={`h-3 w-3 ${regenerating ? "animate-spin" : ""}`} /> Refresh
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="mt-4 rounded-2xl bg-white/60 p-4 text-center text-sm text-zinc-500 dark:bg-zinc-900/60 dark:text-zinc-400">Save the profile to generate your QR.</div>
                    )}
                </div>

                <div className="rounded-3xl border border-zinc-100 bg-white p-5 shadow-soft dark:border-white/10 dark:bg-white/[0.03]" data-testid="emergency-help-card">
                    <div className="flex items-start gap-2">
                        <Heart className="mt-0.5 h-4 w-4 text-rose-500" />
                        <div>
                            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">Need help right now?</p>
                            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">Use the red SOS button on the bottom-left corner to alert your contacts instantly. (Demo: alerts are simulated.)</p>
                        </div>
                    </div>
                </div>
            </aside>
        </div>
    );
};

const Emergency = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <AuthGuard>
                <EmergencyContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Emergency;
